import { useEffect, useMemo, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useParams } from 'react-router-dom';
import { Menu } from 'antd';
import cn from 'classnames';

import beaconLogo from 'assets/BeaconLogo.svg';
import claudeCodeIcon from 'assets/build_claude_code.png';
import chevronGray from 'assets/chevron-gray.svg';

import { DropdownButton, DropdownMenu } from 'Components/DropdownMenu';

import { getFeedbackPreview } from 'services/previewShare';

import copyValue from 'utlis/copy_value';
import { errorReactToastify, successReactToastify } from 'utlis/toasts';

import { buildPreviewPrompt } from './buildPrompt';

import styles from './styles.module.scss';

const BUILD_TOOLS = [
  { key: 'claude-code', name: 'Claude Code', icon: claudeCodeIcon },
];

const PreviewShare = () => {
  const { postProcessUuid } = useParams();
  const [previewPayload, setPreviewPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBuildMenuOpen, setIsBuildMenuOpen] = useState(false);

  useEffect(() => {
    if (!postProcessUuid) {
      setError('Preview link is missing.');
      setIsLoading(false);

      return;
    }

    // eslint-disable-next-line compat/compat
    const abortController = new AbortController();

    const fetchPreview = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const payload = await getFeedbackPreview(postProcessUuid, abortController.signal);

        setPreviewPayload(payload);
      }
      catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        const apiMessage = err?.response?.data?.message || err?.response?.data?.detail;

        setError(apiMessage || 'Unable to load preview right now.');
      }
      finally {
        setIsLoading(false);
      }
    };

    fetchPreview();

    return () => {
      abortController.abort();
    };
  }, [postProcessUuid]);

  const htmlUploadKey = previewPayload?.post_process?.result_data?.html_upload_info?.key;
  const htmlPreviewUrl = htmlUploadKey
    ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${htmlUploadKey}`
    : null;
  const reviewFrameName = previewPayload?.design_review?.extra_data?.review_frame_name;

  // Prompt users can paste into an external AI tool to rebuild this prototype themselves.
  // Null when there's nothing to build a prompt from (hides the dropdown).
  const buildPrompt = useMemo(
    () => (previewPayload ? buildPreviewPrompt(previewPayload) : null),
    [previewPayload]
  );

  const handleBuildInTool = async tool => {
    if (!buildPrompt) {
      return;
    }

    try {
      await copyValue(buildPrompt);
      successReactToastify(`Prompt copied — paste into ${tool.name}`);
    }
    catch (copyError) {
      errorReactToastify('Failed to copy prompt');
    }
  };

  const buildMenu = (
    <Menu
      className={styles.buildInMenu}
      items={BUILD_TOOLS.map(tool => ({
        key: tool.key,
        className: 'p-zero',
        label: (
          <DropdownButton
            className={styles.buildInMenuDropdown}
            icon={<img src={tool.icon} alt="IDE tool" className={styles.buildInMenuIcon} />}
            text={tool.name}
          />
        ),
        onClick: () => handleBuildInTool(tool),
      }))}
    />
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.brand}>
          {reviewFrameName || 'onBeacon'}
        </span>

        <div className={styles.headerActions}>
          {buildPrompt && (
            <DropdownMenu
              overlay={buildMenu}
              trigger={['click']}
              placement="bottomLeft"
              onVisibleChange={setIsBuildMenuOpen}
            >
              <button
                type="button"
                className={cn(styles.buildIn, { [styles.buildInOpen]: isBuildMenuOpen })}
              >
                <span className={styles.buildInText}>
                  Build in
                </span>
                <img
                  src={chevronGray}
                  alt=""
                  className={cn(styles.buildInChevron, { [styles.chevronFlipped]: isBuildMenuOpen })}
                />
              </button>
            </DropdownMenu>
          )}

          <a
            href={`${process.env.REACT_APP_ONBEACON_WEBSITE_URL}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.madeWith}
          >
            <img
              className={styles.madeWithIcon}
              src={beaconLogo}
              alt="onBeacon logo"
            />
            <span className={styles.madeWithText}>
              Made with onBeacon
            </span>
          </a>
        </div>
      </header>

      <main className={styles.content}>
        <section className={styles.card}>
          {isLoading && (
            <div className={styles.loader}>
              <ImpulseSpinner frontColor={'#28BDAE'} />
            </div>
          )}

          {!isLoading && error && (
            <div className={styles.message}>
              <h2>
                Something went wrong
              </h2>
              <p>
                {error}
              </p>
            </div>
          )}

          {!isLoading && !error && !htmlPreviewUrl && (
            <div className={styles.message}>
              <h2>
                Preview unavailable
              </h2>
              <p>
                We couldn&apos;t find a preview to show for this link.
              </p>
            </div>
          )}

          {!isLoading && !error && htmlPreviewUrl && (
            <div className={styles.frameWrapper}>
              <iframe
                title="Design preview"
                src={htmlPreviewUrl}
                className={styles.frame}
                allowFullScreen
              />
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

PreviewShare.propTypes = {};

export default PreviewShare;
